import { useEffect, useRef, useState } from 'react';
import { Box, FormControl, Stack } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';

import { Button, FormLabel } from '@/components/v4';
import PictureViewer from 'components/pictureViewer/PictureViewer';
import FieldError from 'components/forms/new/FieldError';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import useFileUpload from 'hooks/useFileUpload';
import useDetectFileType from 'hooks/useDetectFileType';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';

export const IMAGE_TYPE_ERROR = 'Only png file is allowed.';

/**
 * Template image field. Uses the ImageUploadAndPreview pattern
 * (useFileUpload + useDetectFileType) used elsewhere in the codebase, and
 * the legacy Konva-based PictureViewer for the zoom/pan preview — Chakra's
 * Image component doesn't reproduce the full-image zoom/pan controls the
 * pre-revamp MUI form had.
 *
 * @param {object}   params
 * @param {boolean}  params.darkMode      Current dark mode state.
 * @param {string}   params.existingImage The stored image filename for an existing template (edit mode).
 * @param {function} params.setFieldValue Formik's setFieldValue, used to set `newImage` on selection.
 */
const TemplateImageField = ({ darkMode, existingImage, setFieldValue }) => {
    const fileRef = useRef(null);
    const [typeError, setTypeError] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    const { fileReaderResult, uploadHandler, file } = useFileUpload({
        allowedExtensions: ['.png'],
        fileRef,
    });

    useEffect(() => {
        if (fileReaderResult && file) {
            setFieldValue('newImage', file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileReaderResult]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile && selectedFile.type !== 'image/png') {
            setTypeError(IMAGE_TYPE_ERROR);
            e.target.value = '';
            return;
        }
        setTypeError(null);
        uploadHandler(e);
    };

    const previewSrc =
        fileReaderResult ||
        (existingImage
            ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${existingImage}`
            : null);

    const previewFileType = useDetectFileType(previewSrc);

    return (
        <FormControl
            isInvalid={!!typeError}
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <FormLabel sx={formLabelStyling(darkMode)}>Image</FormLabel>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'flex-start',
                        paddingTop: '15px',
                    }}>
                    <Button
                        as="label"
                        size="sm"
                        sx={{ marginY: '5px', cursor: 'pointer' }}>
                        <AttachmentIcon mr={2} />
                        {file ? file.name : 'Choose Image'}
                        <input
                            style={{ display: 'none' }}
                            type="file"
                            accept=".png"
                            onChange={handleFileChange}
                            ref={fileRef}
                        />
                    </Button>
                    {isHovered && (
                        <FieldToolTip
                            label="Call-flow diagram shown to admins picking this template from the Auto Attendant builder. PNG only."
                            ariaLabel="template-image-tip"
                            left="25px"
                            top="10px"
                        />
                    )}
                </Box>
            </Stack>
            <FieldError isDrawer={false}>{typeError}</FieldError>
            {previewFileType === 'png' && (
                <PictureViewer imageURL={previewSrc} />
            )}
        </FormControl>
    );
};

export default TemplateImageField;
